import { Combobox, Popover, Transition } from "@headlessui/react";
import CountryFlag from "@qoohoo/common/components/CountryFlag"
import { Fragment, useEffect, useState } from "react";
import FieldWrapper from "./FieldWrapper";
import cls from "classnames";
import InfoButton from "components/library/InfoButton";
import getIpInfo from "./api/get-ipinfo";

export default function CountryField({
  label,
  type,
  name,
  placeholder,
  onChange,
  disabled,
  formatValue,
  validator,
  required,
  hint,
  error,
  value,
  options,
  visible,
}) {
  const [userIpInfo, setUserIpInfo] = useState(null);
  const [selected, setSelected] = useState(getDefaultValue());
  const [query, setQuery] = useState("");

  function getDefaultValue() {
    const _defaultValue = placeholder
      ? options.find((option) => optionMatchingLogic(option, placeholder))
      : null;

    return _defaultValue;
  }

  const filteredOptions =
    query === ""
      ? options
      : options.filter((option) => optionMatchingLogic(option, query));

  function optionMatchingLogic(option, query) {
    return option.name
      .toLowerCase()
      .replace(/\s+/g, "")
      .includes(query.toLowerCase().replace(/\s+/g, ""));
  }

  function handleOnSelect(option) {
    setSelected(option);
  }

  function fetchUserIpInfo() {
    if (!userIpInfo) {
      getIpInfo({ setUserIpInfo });
    }
  }

  function updateDefaultCountryWithIpInfo() {
    if (userIpInfo) {
      const _defaultValue = options.find(
        (option) => option.code === userIpInfo?.country
      );
      setSelected(_defaultValue);
    }
  }

  useEffect(() => {
    if (selected) {
      handleOnClick(selected);
    }
  }, [selected]);

  // fetch local country code and set its value
  useEffect(() => {
    fetchUserIpInfo();
  }, []);

  // if local country code updates, then set the default country value
  useEffect(() => {
    updateDefaultCountryWithIpInfo();
  }, [userIpInfo]);

  async function handleOnClick(option) {
    onChange(
      { target: { name, value: option } },
      formatValue,
      validator,
      required
    );
  }
  return (
    <FieldWrapper error={error} hint={hint} visible={visible}>
      <div className={cls("flex flex-col items-start gap-2 w-full")}>
        <div className={cls("flex flex-row items-center gap-1 w-full")}>
          {label && <span className="font-semibold text-sm">{label}</span>}
          <InfoButton description="Select the country where you live, or from where you operate your business. This cannot be changed later." />
        </div>
        <CountryBox
          selected={selected}
          handleOnSelect={handleOnSelect}
          name={name}
          disabled={disabled}
          placeholder={placeholder}
          setQuery={setQuery}
          filteredOptions={filteredOptions}
          query={query}
        />
      </div>
    </FieldWrapper>
  );
}
function CountryBox({
  selected,
  handleOnSelect,
  name,
  disabled,
  placeholder,
  setQuery,
  filteredOptions,
  query,
}) {
  return (
    <Combobox
      value={selected}
      onChange={handleOnSelect}
      name={name}
      disabled={disabled}
    >
      <div className="relative w-full max-w-[420px]">
        <div className="relative w-full cursor-default overflow-hidden rounded-[4px] bg-white text-left border-[1px] border-[#D5D8DC] focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300">
          <Combobox.Button as="div" className="flex items-center">
            <Combobox.Input
              className="w-full border-none py-2 pl-3 pr-10 text-gray-900 focus:ring-0"
              displayValue={(selected) => (selected ? selected.name : "")}
              placeholder={placeholder}
              onChange={(event) => setQuery(event.target.value)}
            />
            <span className="absolute right-2">
              <img src="/assets/icons/cheverondown.png" alt=" " />
            </span>
          </Combobox.Button>
        </div>
        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          afterLeave={() => setQuery("")}
        >
          <Combobox.Options className="z-[99999] absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
            {filteredOptions.length === 0 && query !== "" ? (
              <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                No results
              </div>
            ) : (
              filteredOptions.map((option) => (
                <Combobox.Option
                  key={option.code}
                  className={({ active }) =>
                    `relative cursor-default select-none py-2 pl-6 pr-4 ${
                      active ? "bg-[#384AF1] text-white" : "text-gray-900"
                    }`
                  }
                  value={option}
                >
                  {({ selected, active }) => (
                    <>
                      <div className="flex items-center">
                        <CountryFlag
                          className="w-4 mr-1"
                          countryCode={option.code}
                        />
                        <span
                          className={`block truncate ${
                            selected ? "font-medium" : "font-normal"
                          }`}
                        >
                          {option.name}
                        </span>
                      </div>
                      {selected ? (
                        <span
                          className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                            active ? "text-white" : "text-teal-600"
                          }`}
                        ></span>
                      ) : null}
                    </>
                  )}
                </Combobox.Option>
              ))
            )}
          </Combobox.Options>
        </Transition>
      </div>
    </Combobox>
  );
}
